import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Shield, ArrowLeft, BookOpen, Heart, Lightbulb, ChevronRight,
  BookMarked, Sparkles, HandHeart, Scale
} from 'lucide-react';
import axios from 'axios';
import AppFooter from '../components/AppFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SITUATION_ICONS = {
  overwhelmed_by_debt: Heart,
  starting_the_journey: Sparkles,
  dealing_with_collections: Scale,
  identity_theft: Shield,
  waiting_for_response: BookMarked,
  building_wealth: Lightbulb,
  account_lifecycle: BookOpen
};

export default function FaithStrategiesPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState({});
  const [tips, setTips] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [personalVerse, setPersonalVerse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const [strategiesRes, tipsRes, verseRes] = await Promise.all([
        axios.get(`${API_URL}/api/faith/strategies`, { withCredentials: true }),
        axios.get(`${API_URL}/api/faith/tips`, { withCredentials: true }),
        axios.get(`${API_URL}/api/faith/verse-for-situation`, { withCredentials: true })
      ]);
      setStrategies(strategiesRes.data);
      setTips(tipsRes.data);
      setPersonalVerse(verseRes.data);
    } catch (e) {
      console.error('Failed to load faith content:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#B76E79] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="faith-strategies-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">Selah Slate&trade;</span>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="text-[#36454F]/70 hover:text-[#36454F]">Sign Out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/dashboard" className="inline-flex items-center text-[#36454F]/65 hover:text-[#36454F] font-['Manrope'] mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />Back to Dashboard
        </Link>

        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#36454F]">
            Faith-Based Financial Strategies
          </h1>
          <p className="mt-2 text-[#36454F]/65 font-['Manrope']">
            Biblical wisdom for your financial journey
          </p>
        </div>

        {/* Personal Verse Card */}
        {personalVerse && (
          <Card className="mb-8 border-[#E0BFB8] bg-gradient-to-br from-[#E0BFB8]/20 to-[#E0BFB8]/5 animate-fade-in-up" data-testid="personal-verse-card">
            <CardContent className="p-8">
              <div className="flex items-start space-x-4">
                <HandHeart className="w-8 h-8 text-[#B76E79] flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#B76E79] font-['Manrope'] font-medium mb-3">
                    A Word for Your Season
                  </p>
                  <p className="font-['Playfair_Display'] text-xl italic text-[#36454F] mb-2">
                    "{personalVerse.verse}"
                  </p>
                  <p className="text-sm text-[#36454F]/60 font-['Manrope'] mb-4">— {personalVerse.reference}</p>
                  <p className="text-[#36454F]/80 font-['Manrope'] leading-relaxed">
                    {personalVerse.devotional}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="situations" className="animate-fade-in-up animation-delay-100">
          <TabsList className="bg-white border border-[#36454F]/15 mb-6">
            <TabsTrigger value="situations" className="font-['Manrope'] data-[state=active]:bg-[#B76E79] data-[state=active]:text-white" data-testid="tab-situations">
              <BookMarked className="w-4 h-4 mr-1" strokeWidth={1.5} />Devotionals
            </TabsTrigger>
            <TabsTrigger value="tips" className="font-['Manrope'] data-[state=active]:bg-[#B76E79] data-[state=active]:text-white" data-testid="tab-tips">
              <Lightbulb className="w-4 h-4 mr-1" strokeWidth={1.5} />Tips & Tricks
            </TabsTrigger>
          </TabsList>

          {/* Situations / Devotionals Tab */}
          <TabsContent value="situations">
            {!selectedStrategy ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(strategies).map(([key, strategy], index) => {
                  const Icon = SITUATION_ICONS[key] || BookOpen;
                  return (
                    <Card
                      key={key}
                      className="border-[#36454F]/15 bg-white card-hover cursor-pointer animate-fade-in-up"
                      style={{ animationDelay: `${index * 80}ms` }}
                      onClick={() => setSelectedStrategy({ key, ...strategy })}
                      data-testid={`strategy-card-${key}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 rounded-full bg-[#E0BFB8]/30 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] mb-1">
                              {strategy.title}
                            </h3>
                            <p className="text-sm text-[#36454F]/50 font-['Manrope'] italic line-clamp-2">
                              "{strategy.verse}"
                            </p>
                            <p className="text-xs text-[#36454F]/40 font-['Manrope'] mt-1">
                              {strategy.reference}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-[#36454F]/30 flex-shrink-0" strokeWidth={1.5} />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="animate-fade-in-up">
                <Button variant="ghost" onClick={() => setSelectedStrategy(null)} className="mb-4 text-[#36454F]/65 font-['Manrope']" data-testid="back-to-strategies">
                  <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />All Devotionals
                </Button>

                <Card className="border-[#36454F]/15 bg-white">
                  <CardContent className="p-8">
                    {/* Title & Verse */}
                    <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-4">
                      {selectedStrategy.title}
                    </h2>
                    <div className="p-6 bg-[#E0BFB8]/15 border-l-4 border-[#B76E79] rounded-r-sm mb-6">
                      <p className="font-['Playfair_Display'] text-lg italic text-[#36454F]">
                        "{selectedStrategy.verse}"
                      </p>
                      <p className="text-sm text-[#36454F]/60 font-['Manrope'] mt-2">— {selectedStrategy.reference}</p>
                    </div>

                    {/* Devotional */}
                    <div className="mb-6">
                      <h3 className="font-['Manrope'] font-semibold text-[#36454F] mb-3 flex items-center">
                        <BookOpen className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />
                        Devotional
                      </h3>
                      <p className="text-[#36454F]/80 font-['Manrope'] leading-relaxed">
                        {selectedStrategy.devotional}
                      </p>
                    </div>

                    {/* Practical Steps */}
                    <div className="mb-6">
                      <h3 className="font-['Manrope'] font-semibold text-[#36454F] mb-3 flex items-center">
                        <Lightbulb className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />
                        Practical Steps
                      </h3>
                      <div className="space-y-2">
                        {selectedStrategy.practical_steps?.map((step, i) => (
                          <div key={i} className="flex items-start space-x-3 p-3 bg-[#F5F5F5] rounded-sm">
                            <div className="w-6 h-6 rounded-full bg-[#B76E79] text-white flex items-center justify-center font-['Manrope'] text-xs font-semibold flex-shrink-0 mt-0.5">
                              {i + 1}
                            </div>
                            <p className="text-sm text-[#36454F]/80 font-['Manrope']">{step}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Prayer */}
                    {selectedStrategy.prayer && (
                      <div className="p-6 bg-[#E0BFB8]/10 border border-[#E0BFB8]/30 rounded-sm">
                        <h3 className="font-['Manrope'] font-semibold text-[#36454F] mb-2 flex items-center">
                          <Heart className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />
                          Prayer
                        </h3>
                        <p className="text-[#36454F]/80 font-['Manrope'] italic leading-relaxed">
                          {selectedStrategy.prayer}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Tips & Tricks Tab */}
          <TabsContent value="tips">
            <div className="space-y-4">
              {tips.map((tip, index) => (
                <Card key={index} className="border-[#36454F]/15 bg-white animate-fade-in-up" style={{ animationDelay: `${index * 60}ms` }} data-testid={`tip-card-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 rounded-full bg-[#E0BFB8]/30 flex items-center justify-center flex-shrink-0">
                        <Lightbulb className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-wider text-[#B76E79] font-['Manrope'] font-medium">
                          {tip.category}
                        </span>
                        <p className="mt-1 text-[#36454F] font-['Manrope'] leading-relaxed">
                          {tip.tip}
                        </p>
                        <div className="mt-3 p-3 bg-[#E0BFB8]/10 rounded-sm">
                          <p className="text-sm text-[#36454F]/70 font-['Manrope'] italic">
                            {tip.verse}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Bottom CTA */}
        <div className="mt-12 text-center animate-fade-in-up">
          <Card className="border-[#36454F]/15 bg-white inline-block">
            <CardContent className="p-8">
              <p className="font-['Playfair_Display'] text-lg text-[#36454F] mb-4">
                Ready to take the next step?
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/debt-calculator">
                  <Button className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm" data-testid="goto-calculator-btn">
                    Debt Calculator <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                  </Button>
                </Link>
                <Link to="/upload">
                  <Button variant="outline" className="border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white font-['Manrope'] rounded-sm" data-testid="goto-upload-btn">
                    Dispute Errors <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
